/**
 * HostDensityDashboard.tsx
 *
 * Capacity-planning view for the admin panel. Two layers:
 *
 *   1. Fleet summary card — "across all hosts, where am I?" The single
 *      number used to decide "do I need another OVH box?" Backed by
 *      GET /api/admin/proxmox-hosts/density/fleet.
 *   2. Per-host cards — drill-down for which box is the bottleneck. Backed
 *      by GET /api/admin/proxmox-hosts/:id/density (one call per host).
 *
 * Status traffic light is the load-bearing UI element:
 *   - green  : <70% RAM/disk/CPU committed, plenty of room
 *   - amber  : 70-85% — start sourcing the next host
 *   - red    : >85% RAM/disk/CPU OR swap >50% — DO NOT add VMs here
 *
 * Refreshes every 30 seconds. Don't poll faster — each tick hits multiple
 * upstream Proxmox APIs and we don't want to thrash them.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { getApiUrl } from '../../config/environment';

interface ProxmoxHost {
  id: number;
  name: string;
  hostname: string;
  node_name: string;
  status?: string;
  is_default?: boolean;
}

interface HostDensity {
  host: { id: number; name: string; hostname: string; node_name: string };
  hardware: {
    cpu_cores: number;
    cpu_model: string;
    ram_total_mb: number;
    ram_used_mb: number;
    ram_free_mb: number;
    disk_pool: string;
    disk_total_gb: number;
    disk_used_gb: number;
    swap_total_mb: number;
    swap_used_mb: number;
    ksm_shared_bytes: number;
    load_average: number[];
    uptime_sec: number;
  };
  vms: {
    total: number;
    running: number;
    stopped: number;
    committed_ram_mb: number;
    running_committed_mb: number;
    actual_ram_used_mb: number;
    oversold_ratio: number;
  };
  pressure: {
    ram_pct: number;
    ram_actual_pct: number;
    cpu_pct: number;
    disk_pct: number;
    swap_pct: number;
    status: 'ok' | 'warning' | 'critical';
  };
}

interface FleetDensity {
  hosts: Array<{
    host: { id: number; name: string; hostname: string; node_name: string };
    ok: boolean;
    error?: string;
  }>;
  totals: {
    hosts_total: number;
    hosts_reachable: number;
    running_vms: number;
    ram_total_mb: number;
    ram_committed_mb: number;
    ram_actual_mb: number;
    disk_total_gb: number;
    disk_used_gb: number;
    ram_pct: number;
    disk_pct: number;
    cpu_pct: number;
  } | null;
  worst: { ram_pct: number; disk_pct: number; cpu_pct: number } | null;
  status: 'ok' | 'warning' | 'critical';
}

type DensityLoad =
  | { state: 'loading' }
  | { state: 'ready'; data: HostDensity }
  | { state: 'error'; error: string };

const fmtMB = (mb: number) => {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
};
const fmtGB = (gb: number) => {
  if (gb >= 1024) return `${(gb / 1024).toFixed(2)} TB`;
  return `${gb} GB`;
};
const fmtUptime = (sec: number) => {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
};
const fmtBytes = (b: number) => fmtGB(Math.round(b / 1024 / 1024 / 1024));

// IMPORTANT — this is the one page where we deliberately step outside the
// logo palette. The operational density dashboard needs unmistakable
// red/amber/green status so the on-call human can read it in a glance.
// User explicitly requested this exception ("This page should be in Red
// and Green theme only").
//
// Brand colors stay everywhere else: dashboard hero, KPI tiles, customer
// pages, etc. all use navy + yellow. Status colors live here and here only.
const statusStyles: Record<HostDensity['pressure']['status'], { bg: string; bar: string; label: string; tag: string; icon: string }> = {
  ok:       { bg: 'bg-green-50 border-green-200',   bar: 'bg-green-500',  label: 'Healthy',  tag: 'bg-green-100 text-green-800',  icon: '✓'  },
  warning:  { bg: 'bg-amber-50 border-amber-200',   bar: 'bg-amber-500',  label: 'Warning',  tag: 'bg-amber-100 text-amber-800',  icon: '⚠️' },
  critical: { bg: 'bg-red-50   border-red-300',     bar: 'bg-red-600',    label: 'Critical', tag: 'bg-red-100   text-red-800',    icon: '🚨' },
};

/**
 * Tiny labelled bar. Used 3 times per host (RAM/CPU/disk) and 3 times in the
 * fleet summary. Color picks pressure tier per-bar so a card with green RAM
 * but red disk still flags red on the disk line.
 */
const PressureBar: React.FC<{
  label: string;
  pct: number;
  rightText?: string;
  hint?: string;
}> = ({ label, pct, rightText, hint }) => {
  const tier: HostDensity['pressure']['status'] =
    pct >= 85 ? 'critical' : pct >= 70 ? 'warning' : 'ok';
  const barColor = statusStyles[tier].bar;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-gray-700 font-medium">{label}</span>
        {rightText && <span className="text-gray-900 font-mono">{rightText}</span>}
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div className={`${barColor} h-2 rounded-full transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <div className="text-xs text-gray-500 mt-1">{pct.toFixed(1)}%{hint ? ` · ${hint}` : ''}</div>
    </div>
  );
};

/**
 * Editable host name. The DB column is `proxmox_hosts.name` and every page
 * that renders a host reads from it, so renaming here propagates everywhere
 * (VPS list, firewall page, etc.) on next refresh. Reuses the existing
 * PUT /api/admin/proxmox-hosts/:id endpoint — no new wiring needed.
 */
const EditableHostName: React.FC<{ id: number; name: string; onSaved: () => void }> = ({ id, name, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset local state when the parent passes a different name (e.g. after
  // a re-fetch following save). Without this, the input would stick on the
  // old value the next time we open editing.
  useEffect(() => { setValue(name); }, [name]);

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === name) { setEditing(false); return; }
    setSaving(true);
    setErr(null);
    try {
      const r = await fetch(getApiUrl(`/admin/proxmox-hosts/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ name: trimmed }),
      });
      const j = await r.json();
      if (!j.success) {
        setErr(j.error || 'Save failed');
      } else {
        setEditing(false);
        onSaved();
      }
    } catch (e: any) {
      setErr(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    // Pencil is always visible (not hover-gated). The hover-reveal version
    // looked broken to anyone who didn't know it existed and was invisible
    // on touch devices.
    return (
      <div className="flex items-center gap-2">
        <h3 className="font-semibold text-primary-navy">{name}</h3>
        <button
          onClick={() => setEditing(true)}
          className="inline-flex items-center gap-1 text-xs text-primary-navy hover:text-primary-700 px-1.5 py-0.5 rounded hover:bg-secondary-50"
          title="Rename"
        >
          ✏️ <span>Rename</span>
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <input
        autoFocus
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setValue(name); setEditing(false); } }}
        disabled={saving}
        className="px-2 py-0.5 border border-gray-300 rounded text-sm font-semibold text-primary-navy bg-white focus:outline-none focus:ring-2 focus:ring-secondary-500"
      />
      <button
        onClick={save}
        disabled={saving}
        className="text-xs px-2 py-0.5 bg-primary-navy text-white rounded hover:bg-primary-700 disabled:opacity-50"
      >
        {saving ? '…' : 'Save'}
      </button>
      <button
        onClick={() => { setValue(name); setEditing(false); setErr(null); }}
        disabled={saving}
        className="text-xs text-gray-500 hover:text-gray-700"
      >
        Cancel
      </button>
      {err && <span className="text-xs text-primary-navy font-medium ml-1">{err}</span>}
    </div>
  );
};

const HostCard: React.FC<{ host: ProxmoxHost; densityState: DensityLoad; onRenamed: () => void }> = ({ host, densityState, onRenamed }) => {
  if (densityState.state === 'loading') {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-1/3 mb-3"></div>
        <div className="h-3 bg-gray-100 rounded w-2/3"></div>
      </div>
    );
  }
  if (densityState.state === 'error') {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">{host.name}</h3>
            <p className="text-xs text-gray-500 font-mono">{host.hostname}</p>
          </div>
          <span className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-700">Unreachable</span>
        </div>
        <p className="mt-3 text-sm text-gray-500">{densityState.error}</p>
      </div>
    );
  }

  const d = densityState.data;
  const s = statusStyles[d.pressure.status];
  const ksmGB = d.hardware.ksm_shared_bytes / 1024 / 1024 / 1024;

  return (
    <div className={`border rounded-xl p-6 ${s.bg}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <EditableHostName id={d.host.id} name={d.host.name} onSaved={onRenamed} />
          <p className="text-xs text-gray-500 font-mono">{d.host.hostname} · {d.host.node_name}</p>
          <p className="text-xs text-gray-400 mt-1">{d.hardware.cpu_model}</p>
        </div>
        <span className={`text-xs px-2 py-1 rounded font-medium inline-flex items-center gap-1 ${s.tag}`}>
          <span aria-hidden>{s.icon}</span>{s.label}
        </span>
      </div>

      <div className="space-y-3">
        {/* The headline metric: how much physical RAM is promised to running
            VMs. This is what gates "can I sell another VPS without this box
            going OOM." Actual usage is the secondary signal (are customers
            using what they bought, or sitting idle). */}
        <PressureBar
          label="RAM committed (running VMs)"
          pct={d.pressure.ram_pct}
          rightText={`${fmtMB(d.vms.running_committed_mb)} / ${fmtMB(d.hardware.ram_total_mb)}`}
          hint="commitment"
        />
        <PressureBar
          label="RAM actually used"
          pct={d.pressure.ram_actual_pct}
          rightText={`${fmtMB(d.vms.actual_ram_used_mb)} / ${fmtMB(d.hardware.ram_total_mb)}`}
          hint="touched by guests"
        />
        <PressureBar
          label="CPU"
          pct={d.pressure.cpu_pct}
          rightText={`load ${d.hardware.load_average.map(l => l.toFixed(2)).join(' / ') || '–'}`}
          hint={`${d.hardware.cpu_cores} cores`}
        />
        <PressureBar
          label={`Disk (${d.hardware.disk_pool})`}
          pct={d.pressure.disk_pct}
          rightText={`${fmtGB(d.hardware.disk_used_gb)} / ${fmtGB(d.hardware.disk_total_gb)}`}
        />
        {d.hardware.swap_total_mb > 0 && (
          <PressureBar
            label="Swap"
            pct={d.pressure.swap_pct}
            rightText={`${fmtMB(d.hardware.swap_used_mb)} / ${fmtMB(d.hardware.swap_total_mb)}`}
            // Linux swaps proactively even with RAM headroom, so a high swap
            // number doesn't necessarily mean trouble. We only call it real
            // pressure when actual RAM usage is also high; otherwise hint
            // that kswapd is just doing housekeeping.
            hint={d.pressure.swap_pct >= 50 && d.pressure.ram_actual_pct >= 75
              ? '⚠ host is memory-pressured'
              : 'kernel housekeeping'}
          />
        )}

        <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-200/60 text-xs">
          <div>
            <div className="text-gray-500">VMs</div>
            <div className="font-semibold text-gray-900">
              {d.vms.running} <span className="text-gray-400 font-normal">/ {d.vms.total}</span>
            </div>
            <div className="text-gray-400">{d.vms.stopped} stopped</div>
          </div>
          <div>
            <div className="text-gray-500">Oversold</div>
            <div className={`font-semibold ${d.vms.oversold_ratio >= 1 ? 'text-red-600' : 'text-gray-900'}`}>
              {d.vms.oversold_ratio.toFixed(2)}×
            </div>
            <div className="text-gray-400">{fmtMB(d.hardware.ram_free_mb)} free</div>
          </div>
          <div>
            <div className="text-gray-500">KSM saved</div>
            <div className="font-semibold text-gray-900">{ksmGB.toFixed(1)} GB</div>
            <div className="text-gray-400">up {fmtUptime(d.hardware.uptime_sec)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Fleet summary card — sum across all hosts plus the worst-host per
 * resource. Worst is what triggers "buy another box" alerts: averaging
 * away one critical host with 4 idle ones would hide the problem.
 */
const FleetSummary: React.FC<{ fleet: FleetDensity | null; loading: boolean }> = ({ fleet, loading }) => {
  if (loading || !fleet) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 animate-pulse">
        <div className="h-5 bg-gray-200 rounded w-1/4 mb-3"></div>
        <div className="grid grid-cols-3 gap-4">
          <div className="h-12 bg-gray-100 rounded"></div>
          <div className="h-12 bg-gray-100 rounded"></div>
          <div className="h-12 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }
  if (!fleet.totals || !fleet.worst) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-sm text-gray-500">
        No reachable hosts.
      </div>
    );
  }
  const s = statusStyles[fleet.status];
  const t = fleet.totals;
  const w = fleet.worst;

  return (
    <div className={`border rounded-xl p-6 ${s.bg}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">Fleet capacity</h3>
          <p className="text-xs text-gray-600">
            {t.hosts_reachable} of {t.hosts_total} hosts reachable · {t.running_vms} running VMs
          </p>
        </div>
        <span className={`text-xs px-2 py-1 rounded font-medium ${s.tag}`}>{s.label}</span>
      </div>

      {/* Two-column: totals (sum-of-fleet) on the left, worst host % on the
          right. The right column is the one to watch — one box at 95% is
          the signal to add capacity, even if the fleet average looks fine. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="text-xs font-medium text-gray-700 uppercase tracking-wide">Fleet totals</div>
          <PressureBar
            label="RAM committed"
            pct={t.ram_pct}
            rightText={`${fmtMB(t.ram_committed_mb)} / ${fmtMB(t.ram_total_mb)}`}
          />
          <PressureBar
            label="Disk used"
            pct={t.disk_pct}
            rightText={`${fmtGB(t.disk_used_gb)} / ${fmtGB(t.disk_total_gb)}`}
          />
          <PressureBar
            label="CPU (average)"
            pct={t.cpu_pct}
            hint="across reachable hosts"
          />
        </div>
        <div className="space-y-3">
          <div className="text-xs font-medium text-gray-700 uppercase tracking-wide">
            Worst host (the one to watch)
          </div>
          <PressureBar label="RAM" pct={w.ram_pct} />
          <PressureBar label="Disk" pct={w.disk_pct} />
          <PressureBar label="CPU" pct={w.cpu_pct} />
        </div>
      </div>

      {fleet.status === 'critical' && (
        <div className="mt-4 pt-3 border-t border-red-200/60 text-sm text-red-700">
          <strong>🚨 Time to buy another OVH box.</strong> At least one host is past the safe-sell
          threshold. New provisioning will pile up on the same overloaded box unless you stop it
          or add capacity.
        </div>
      )}
      {fleet.status === 'warning' && (
        <div className="mt-4 pt-3 border-t border-amber-200/60 text-sm text-amber-800">
          ⚠️ A host is approaching saturation. Start sourcing the next box now — provisioning lead
          time on OVH is hours-to-days.
        </div>
      )}
    </div>
  );
};

// Poll cadence for live updates. Five seconds × 3 hosts × 3 upstream
// Proxmox API calls each = ~36 req/min — fine for an admin panel watched
// by 1-2 people. Increase if Proxmox starts rate-limiting.
const POLL_INTERVAL_MS = 5_000;

const HostDensityDashboard: React.FC = () => {
  const [hosts, setHosts] = useState<ProxmoxHost[] | null>(null);
  // densities is the *last known* state per host. We never reset back to
  // {state:'loading'} on subsequent polls — that would flash the skeleton
  // on every 5s tick. Initial mount: empty record → cards show skeleton
  // for one render, then sticky.
  const [densities, setDensities] = useState<Record<number, DensityLoad>>({});
  const [fleet, setFleet] = useState<FleetDensity | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  // Last successful update timestamp + a brief flash flag to signal that
  // the page just refreshed. The flash is what makes 5s polling *feel*
  // live without strobing the whole UI.
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [pulse, setPulse] = useState(false);

  const token = () => localStorage.getItem('token');

  const loadHosts = useCallback(async () => {
    try {
      const r = await fetch(getApiUrl('/admin/proxmox-hosts'), {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const j = await r.json();
      if (j.success && Array.isArray(j.data)) setHosts(j.data);
    } catch (e) {
      console.error('Failed to load hosts:', e);
    }
  }, []);

  // Note: `loadDensity` does NOT set loading state mid-refresh. If a host
  // already has a `ready` entry, we keep showing the last numbers while
  // the request is in flight, then atomically swap to the new ones. Only
  // the very first fetch ever transitions through `loading`.
  const loadDensity = useCallback(async (id: number) => {
    setDensities(prev => prev[id] ? prev : { ...prev, [id]: { state: 'loading' } });
    try {
      const r = await fetch(getApiUrl(`/admin/proxmox-hosts/${id}/density`), {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const j = await r.json();
      if (j.success && j.data) {
        setDensities(prev => ({ ...prev, [id]: { state: 'ready', data: j.data as HostDensity } }));
      } else {
        // Only show error if we don't already have a successful snapshot —
        // otherwise keep the last-good and just skip this tick.
        setDensities(prev => prev[id]?.state === 'ready'
          ? prev
          : { ...prev, [id]: { state: 'error', error: j.error || 'Failed to load density' } });
      }
    } catch (e: any) {
      setDensities(prev => prev[id]?.state === 'ready'
        ? prev
        : { ...prev, [id]: { state: 'error', error: e?.message || 'Network error' } });
    }
  }, []);

  const loadFleet = useCallback(async () => {
    try {
      const r = await fetch(getApiUrl('/admin/proxmox-hosts/density/fleet'), {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const j = await r.json();
      if (j.success && j.data) setFleet(j.data as FleetDensity);
    } catch (e) {
      console.error('Failed to load fleet density:', e);
    }
  }, []);

  // Fan-out across hosts + fleet, then update the "just refreshed" pulse
  // when ALL requests have settled (using allSettled so one slow host
  // doesn't block the rest). The pulse is 400ms — long enough to see,
  // short enough not to be annoying at a 5s cadence.
  const refreshAll = useCallback(async () => {
    if (!hosts) return;
    await Promise.allSettled([
      ...hosts.map(h => loadDensity(h.id)),
      loadFleet(),
    ]);
    setLastUpdate(new Date());
    setPulse(true);
    setTimeout(() => setPulse(false), 400);
  }, [hosts, loadDensity, loadFleet]);

  useEffect(() => { loadHosts(); }, [loadHosts]);

  // Drive a refresh on (hosts loaded) and every refreshTick. The tick
  // setter is the only thing the interval touches — refreshAll runs as a
  // side-effect of tick changing, which keeps the dependency graph clean.
  useEffect(() => { refreshAll(); }, [refreshTick, refreshAll]);

  useEffect(() => {
    const t = setInterval(() => setRefreshTick(x => x + 1), POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  // "Live" pill in the top-right that pulses on every successful refresh
  // and shows "updated Ns ago" next to it. Cheap visual cue that the page
  // is genuinely refreshing without forcing the user to watch numbers
  // tick to be sure.
  const secondsAgo = lastUpdate ? Math.max(0, Math.floor((Date.now() - lastUpdate.getTime()) / 1000)) : null;
  // Force re-render of the "Ns ago" string every second even when no
  // refresh has happened. Lightweight — single setState per second.
  const [, setNow] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNow(n => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Host Density</h2>
          <p className="text-sm text-gray-600 mt-1">
            Live per-host CPU, RAM and disk commitment across the OVH fleet. Use this to decide
            when to provision another box. Refreshes every {POLL_INTERVAL_MS / 1000} seconds.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm">
          <span className={`h-2 w-2 rounded-full bg-green-500 ${pulse ? 'animate-ping' : ''}`} />
          <span className="text-xs font-medium text-gray-700">Live</span>
          {secondsAgo !== null && (
            <span className="text-xs text-gray-500 tabular-nums">
              · updated {secondsAgo < 1 ? 'now' : `${secondsAgo}s ago`}
            </span>
          )}
        </div>
      </div>

      <FleetSummary fleet={fleet} loading={!fleet} />

      {!hosts ? (
        <div className="text-gray-500">Loading hosts…</div>
      ) : hosts.length === 0 ? (
        <div className="text-gray-500">No Proxmox hosts registered.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {hosts.map(h => (
            <HostCard
              key={h.id}
              host={h}
              densityState={densities[h.id] || { state: 'loading' }}
              onRenamed={() => { loadHosts(); loadDensity(h.id); loadFleet(); }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HostDensityDashboard;
